import { useState, useEffect, useContext, useCallback } from "react";
import { supabase } from "./supabaseClient.js";
import { ThemeContext } from "./ThemeContext.jsx";

// ═══════════════════════════════════════════════════════════════════════════
// STORY LINKS MANAGER
//
// Phase 1 UI for the explicit story-linking feature that complements the
// May 20 story-isolation work (PR #79). Backed by the existing
// conversation_links table.
//
// What it does:
//   - Shows which OTHER stories the active story is currently linked to.
//   - Lets the user link the active story to another of their stories.
//   - Lets the user unlink.
//
// Why it exists:
//   By default (post PR #79) voice samples for an active story are
//   scoped to the current story + Discord. App-mirrored samples from
//   OTHER stories are excluded so cross-scene bleed is impossible.
//   This UI is the opt-in: if the user actually DOES want voice
//   continuity across stories (e.g. a multi-story arc with the same
//   characters), linking re-enables retrieval from those linked
//   stories' app rows.
//
// What it doesn't do (yet):
//   - Bidirectional symmetric links (insert is a single row;
//     match_character_voice handles either direction via the OR check
//     on a_id/b_id in the caller's resolver).
//   - Linking DM conversations to stories (the conversation_links
//     table supports it via the type column, but the voice-isolation
//     RPC currently only accepts story_ids).
// ═══════════════════════════════════════════════════════════════════════════

function buildTheme(theme) {
  return {
    bg: theme?.bgBase || "#1a1a2e",
    bgSecondary: theme?.bgSecondary || "#2f3136",
    bgCard: theme?.bgElevated || "#2b2d31",
    border: theme?.borderDark || "#202225",
    borderSecondary: theme?.borderMedium || "#40444b",
    text: theme?.textPrimary || "#dcddde",
    textMuted: theme?.textMuted || "#949ba4",
    accent: theme?.accentPrimary || "#d1b3ff",
    error: theme?.accentDanger || "#ed4245",
    success: theme?.accentSuccess || "#57f287",
  };
}

export default function StoryLinksManager({ user, activeStoryId, activeStoryName, onClose }) {
  const themeContext = useContext(ThemeContext);
  const T = buildTheme(themeContext?.theme);

  const [allStories, setAllStories] = useState([]);
  const [linkedIds, setLinkedIds] = useState([]); // story IDs linked to active
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    if (!user?.id) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      // 1. All of the user's stories (so they can pick one to link).
      const { data: stories, error: storiesErr } = await supabase
        .from("stories")
        .select("id, name, last_active_at")
        .eq("user_id", user.id)
        .is("deleted_at", null)
        .order("last_active_at", { ascending: false, nullsFirst: false });
      if (storiesErr) throw storiesErr;
      setAllStories(stories || []);

      // 2. Existing links — both directions (a_id=active OR b_id=active)
      if (activeStoryId) {
        const { data: links, error: linksErr } = await supabase
          .from("conversation_links")
          .select("a_type, a_id, b_type, b_id")
          .eq("user_id", user.id)
          .or(`a_id.eq.${activeStoryId},b_id.eq.${activeStoryId}`);
        if (linksErr) throw linksErr;
        const linkedSet = new Set();
        for (const row of links || []) {
          if (row.a_id === activeStoryId && row.b_type === "story") linkedSet.add(row.b_id);
          if (row.b_id === activeStoryId && row.a_type === "story") linkedSet.add(row.a_id);
        }
        setLinkedIds(Array.from(linkedSet));
      } else {
        setLinkedIds([]);
      }
    } catch (err) {
      console.error("[StoryLinksManager] fetch failed:", err);
      setError(err?.message || String(err));
    } finally {
      setLoading(false);
    }
  }, [user?.id, activeStoryId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const addLink = async (otherStoryId) => {
    if (!activeStoryId || !user?.id) return;
    setSaving(otherStoryId);
    try {
      // Normalize: always store with the smaller UUID as a_id for dedupe.
      const [a_id, b_id] = [activeStoryId, otherStoryId].sort();
      const { error: insErr } = await supabase
        .from("conversation_links")
        .insert({
          user_id: user.id,
          a_type: "story", a_id,
          b_type: "story", b_id,
        });
      if (insErr) throw insErr;
      setLinkedIds((prev) => prev.includes(otherStoryId) ? prev : [...prev, otherStoryId]);
    } catch (err) {
      console.error("[StoryLinksManager] addLink failed:", err);
      alert(`Link failed: ${err?.message || err}`);
    } finally {
      setSaving(null);
    }
  };

  const removeLink = async (otherStoryId) => {
    if (!activeStoryId || !user?.id) return;
    if (!confirm("Unlink this story? The current story will stop pulling voice samples from it.")) return;
    setSaving(otherStoryId);
    try {
      const [a_id, b_id] = [activeStoryId, otherStoryId].sort();
      const { error: delErr } = await supabase
        .from("conversation_links")
        .delete()
        .eq("user_id", user.id)
        .eq("a_id", a_id)
        .eq("b_id", b_id);
      if (delErr) throw delErr;
      setLinkedIds((prev) => prev.filter((id) => id !== otherStoryId));
    } catch (err) {
      console.error("[StoryLinksManager] removeLink failed:", err);
      alert(`Unlink failed: ${err?.message || err}`);
    } finally {
      setSaving(null);
    }
  };

  if (!activeStoryId) {
    return (
      <Wrapper T={T} onClose={onClose}>
        <div style={{ color: T.textMuted, fontSize: 13, padding: 20, textAlign: "center" }}>
          Open a story first, then come back here to link it to another story.
        </div>
      </Wrapper>
    );
  }

  const linkedSet = new Set(linkedIds);
  const linkedStories = allStories.filter((s) => linkedSet.has(s.id));
  const otherStories = allStories.filter((s) => s.id !== activeStoryId && !linkedSet.has(s.id));

  return (
    <Wrapper T={T} onClose={onClose}>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 12, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4 }}>
          Active story
        </div>
        <div style={{ fontSize: 15, fontWeight: 600, color: T.text }}>
          {activeStoryName || activeStoryId.slice(0, 8)}
        </div>
        <div style={{ fontSize: 11, color: T.textMuted, marginTop: 6, lineHeight: 1.5 }}>
          By default, voice samples for this story come from Discord history + this story's own mirrored messages. Link OTHER stories below to also pull their voice samples — useful when you want voice continuity across a multi-story arc with the same characters.
        </div>
      </div>

      {loading ? (
        <div style={{ color: T.textMuted, padding: 20 }}>Loading...</div>
      ) : error ? (
        <div style={{ padding: 20 }}>
          <div style={{ color: T.error, fontSize: 13 }}>Error: {error}</div>
          <button onClick={fetchData} style={{ marginTop: 12, padding: "6px 14px", background: T.accent, color: "white", border: "none", borderRadius: 6, cursor: "pointer" }}>Retry</button>
        </div>
      ) : (
        <>
          {/* Currently linked */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8 }}>
              Currently linked ({linkedStories.length})
            </div>
            {linkedStories.length === 0 ? (
              <div style={{ fontSize: 12, color: T.textMuted, fontStyle: "italic", padding: "8px 0" }}>
                No linked stories. This story is fully isolated (default).
              </div>
            ) : (
              linkedStories.map((s) => (
                <div key={s.id} style={{
                  display: "flex", alignItems: "center", gap: 8,
                  padding: "8px 12px",
                  backgroundColor: T.bgCard,
                  border: `1px solid ${T.accent}55`,
                  borderRadius: 6,
                  marginBottom: 6,
                }}>
                  <div style={{ flex: 1, fontSize: 13, color: T.text }}>{s.name}</div>
                  <button
                    onClick={() => removeLink(s.id)}
                    disabled={saving === s.id}
                    style={{
                      padding: "4px 12px", fontSize: 11, borderRadius: 4,
                      border: `1px solid ${T.error}55`,
                      background: "transparent",
                      color: T.error,
                      cursor: saving === s.id ? "wait" : "pointer",
                      opacity: saving === s.id ? 0.5 : 1,
                    }}
                  >
                    {saving === s.id ? "..." : "Unlink"}
                  </button>
                </div>
              ))
            )}
          </div>

          {/* Available to link */}
          <div>
            <div style={{ fontSize: 12, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8 }}>
              Link another story ({otherStories.length} available)
            </div>
            {otherStories.length === 0 ? (
              <div style={{ fontSize: 12, color: T.textMuted, fontStyle: "italic", padding: "8px 0" }}>
                No other stories to link. Create another story first.
              </div>
            ) : (
              <div style={{ maxHeight: 280, overflowY: "auto" }}>
                {otherStories.map((s) => (
                  <div key={s.id} style={{
                    display: "flex", alignItems: "center", gap: 8,
                    padding: "8px 12px",
                    borderBottom: `1px solid ${T.border}55`,
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, color: T.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.name}</div>
                      {s.last_active_at && (
                        <div style={{ fontSize: 10, color: T.textMuted }}>
                          Active {new Date(s.last_active_at).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => addLink(s.id)}
                      disabled={saving === s.id}
                      style={{
                        padding: "4px 12px", fontSize: 11, borderRadius: 4,
                        border: `1px solid ${T.accent}77`,
                        background: T.accent + "22",
                        color: T.accent,
                        cursor: saving === s.id ? "wait" : "pointer",
                        opacity: saving === s.id ? 0.5 : 1,
                        fontWeight: 600,
                      }}
                    >
                      {saving === s.id ? "..." : "🔗 Link"}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </Wrapper>
  );
}

function Wrapper({ children, T, onClose }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 1500,
        backgroundColor: "rgba(0,0,0,0.6)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: T.bg,
          border: `1px solid ${T.borderSecondary}`,
          borderRadius: 10,
          width: "100%", maxWidth: 520,
          maxHeight: "90vh",
          overflow: "hidden",
          display: "flex", flexDirection: "column",
        }}
      >
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: "14px 18px",
          borderBottom: `1px solid ${T.borderSecondary}`,
          backgroundColor: T.bgSecondary,
        }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: T.text }}>
            🔗 Story Links — voice continuity across stories
          </div>
          <button
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              color: T.textMuted,
              fontSize: 20,
              cursor: "pointer",
              padding: "0 8px",
              lineHeight: 1,
            }}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div style={{ padding: 18, overflowY: "auto", flex: 1 }}>
          {children}
        </div>
      </div>
    </div>
  );
}
